/**
 * TurnTo - Ratings and Reviews
 * Functions that support the TurnTo ratings and reviews
 * implementation. This is meant for small enhancements
 * that help bridge the gap between TurnTo capabilities
 * and UI/UX requirements.
 */
export const ttRatingsAndReviews = {
  mounted() {
    const reviewsContainer = this.el;
    const WRITE_A_REVIEW = document.getElementById('tt-reviews-summary')?.dataset.ctaText;
    const REVIEWS_HEADING_SELECTOR =
      '.tt-c-reviews-summary__content .tt-c-reviews-summary__heading';
    const NO_REVIEWS_HEADING_SELECTOR =
      '.tt-c-reviews-summary__no-reivews .tt-c-reviews-summary__heading';
    const REVIEWS_SUMMARY_SELECTOR =
      '.tt-w-reviews-summary .tt-c-reviews-summary__content .tt-l-grid__row.tt-c-reviews-summary__grid-row:nth-child(2)';
    const NO_REVIEWS_SUMMARY_SELECTOR =
      '.tt-c-reviews-summary__no-reivews .tt-c-reviews-summary__no-reivews-body.tt-u-spacing--md';
    const WAIT_TIME = 500;
    const REVIEWS_WIDGET_HEADER_SELECTOR = '.ratings-and-reviews .ratings-and-reviews-header';

    /**
     * Creates an anchor tag in this format:
     * <a href=[url] class="write-review-cta"></a>
     * @param skuParam string
     * @returns HTMLElement
     */
    const createCTALink = (skuParam: string) => {
      const ctaLink = document.createElement('a');
      ctaLink.innerText = WRITE_A_REVIEW ?? '';
      ctaLink.classList.add('write-review-cta');
      ctaLink.setAttribute('href', `/write-a-review?sku=${skuParam}`);
      ctaLink.addEventListener('click', () => {
        window.dispatchEvent(
          new CustomEvent('pdp.onReviewStart', { detail: { prodID: skuParam } })
        );
      });
      return ctaLink;
    };

    /**
     * Inserts a review count total into the TurnTo (TT) Review Summary widget UI
     * The review count format: (X)
     * The review summary widget has 2 states:
     *   1) at least 1 review - e.g. (23)
     *   2) no reviews - e.g. (0)
     * The TT widget will render different HTML elements based on these states. This
     * function determines the Review Summary state and inserts the appropriate text.
     * @param reviewsContainer HTMLElement
     */
    const insertReviewTotal = () => {
      const reviewsText: string = (
        reviewsContainer.querySelector(REVIEWS_HEADING_SELECTOR) as HTMLElement
      )?.innerText;
      const noNoReviewsHeading: HTMLElement | null = reviewsContainer.querySelector(
        NO_REVIEWS_HEADING_SELECTOR
      );

      // If there are reviews, then use js to parse the reviews count string from the HTML
      // and then insert that total as a new div that contains the reviews count.
      // For example: <div class="ratings-total">(34)</div>
      if (reviewsText) {
        const reviewsTotal = parseInt(reviewsText);
        const starsDiv: HTMLElement | null = reviewsContainer.querySelector(
          '.tt-c-reviews-summary__stars'
        );
        const ratingsCountDiv = document.createElement('div');
        ratingsCountDiv.classList.add('ratings-total');
        ratingsCountDiv.innerText = `(${reviewsTotal})`;
        starsDiv?.appendChild(ratingsCountDiv);
      }

      // If this is a no reviews UI, then use CSS to insert a zero :before the stars
      // and a "(0)" :after the stars
      if (noNoReviewsHeading) {
        const starsContainer: HTMLElement | null = reviewsContainer.querySelector(
          '.tt-c-reviews-summary__no-reivews .tt-c-rating.tt-c-rating--actionable'
        );
        starsContainer?.classList.add('no-reviews');
      }
    };

    // insertCta() is a function that inserts a CTA button into the PDP Review
    // Summary widget. This was done so that when a user attempts to write a review, the
    // user is directed to a stand alone brand page with a header and footer that contains
    // the TurnTo write a review form instead of the default behavior, a modal. This CTA looks
    // like <a href="/write-a-review page?sku=[PRODUCT SKU]">. This is a P0 implementation.
    // Write A Review Form (warf) dependencies:
    //   sku          - product id
    //   turnToConfig - object that contains config values for TurnTo widgets
    //     Example of how this object is initialized:
    //       var turnToConfig = {
    //         locale: "en_US",
    //         pageId: "pdp-page",
    //         sku: "AEX201",
    //       };
    const insertCta = () => {
      // Only attempt inserting the CTA if Review Summary HTML exists
      const reviewSummaryContainer = reviewsContainer.querySelector(REVIEWS_SUMMARY_SELECTOR);
      const noReviewsContainer = reviewsContainer.querySelector(NO_REVIEWS_SUMMARY_SELECTOR);

      if (reviewSummaryContainer) {
        const ctaLink = createCTALink(window.turnToConfig.sku);
        reviewSummaryContainer?.appendChild(ctaLink);
      }

      if (noReviewsContainer) {
        const ctaLink = createCTALink(window.turnToConfig.sku);
        noReviewsContainer?.appendChild(ctaLink);
      }
    };

    // Callback function for reviewsList onUpdate event
    const dispatchPDPReviewSort = () => {
      const reviewSortSelected = event.currentTarget.responseURL.split('/');
      const reviewSort = {
        sortVal: reviewSortSelected[13],
        prodID: reviewSortSelected[6]
      };

      window.dispatchEvent(new CustomEvent('pdp.onReviewSort', { detail: reviewSort }));
    };

    const showWidgetHeader = () => {
      const header = document.querySelector(REVIEWS_WIDGET_HEADER_SELECTOR);
      header?.classList.remove('hidden');
    };

    const initTurnToConfigs = () => {
      const sku = reviewsContainer?.dataset.sku;
      window.turnToConfig = {
        locale: reviewsContainer.dataset.locale,
        pageId: 'pdp-page',
        sku,
        reviewsList: {
          onUpdate: dispatchPDPReviewSort,
          onFinish: function () {
            setTimeout(() => {
              showWidgetHeader();
              insertReviewTotal();
              insertCta();
            }, WAIT_TIME);
          }
        }
      };
      window.TurnToCmd =
        window.TurnToCmd ||
        function (...args) {
          (TurnToCmd.q = TurnToCmd.q || []).push(args);
        };
    };

    const loadTurnToScript = () => {
      const script = document.createElement('script');
      const siteKey = reviewsContainer.dataset.siteKey;
      const domainExt = reviewsContainer.dataset.domainExt;
      script.async = true;
      script.src = `https://widgets.turnto.${domainExt}/v5/widgets/${siteKey}/js/turnto.js`;
      document.body?.appendChild(script);
    };

    initTurnToConfigs();
    loadTurnToScript();
  }
};
